import axios, { AxiosRequestConfig } from 'axios';

// 1. Base URL configuration
const resolveApiUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    if ((envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))
      && window.location.hostname && window.location.hostname !== 'localhost'
      && window.location.hostname !== '127.0.0.1') {
      return envUrl.replace(/localhost|127\.0\.0\.1/, window.location.hostname);
    }
    return envUrl;
  }

  const port = window.location.port;
  if (port && port !== '8000') {
    return `http://${window.location.hostname}:8000/api`;
  }
  return '/api';
};

const API_URL = resolveApiUrl();

const api = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetry = (error: any): boolean => {
  const status = error?.response?.status;
  const code = error?.code;

  return !status || status >= 500 || status === 429 || code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT';
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (!config || !shouldRetry(error)) {
      return Promise.reject(error);
    }

    config.__retryCount = config.__retryCount ?? 0;
    if (config.__retryCount >= 2) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;
    await sleep(500 * config.__retryCount);
    return api(config);
  }
);

// ── In-Memory Response Cache (5-minute TTL) ────────────────────────────────
const responseCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// ── In-Flight Request Deduplication ───────────────────────────────────────
// If a GET for the same URL is already in-flight, we return the same Promise
// instead of launching another HTTP request. This eliminates the double-fire
// caused by React Strict Mode mounting components twice.
const inFlightRequests = new Map<string, Promise<any>>();

// ── Scoped Cache Invalidation ─────────────────────────────────────────────
// Maps URL prefixes of mutated resources to the cache-key prefixes that
// should be invalidated. Much smarter than clearing everything on any write.
const INVALIDATION_MAP: Record<string, string[]> = {
  '/students':      ['/students', '/classes/all-pupils', '/school/summary'],
  '/teachers':      ['/teachers', '/school/summary'],
  '/classes':       ['/classes', '/manage-classes', '/school/summary'],
  '/manage-classes':['/manage-classes', '/classes', '/school/summary'],
  '/subjects':      ['/subjects', '/school/summary'],
  '/settings':      ['/settings'],
  '/attendance':    ['/attendance'],
  '/results':       ['/assessments', '/reports'],
  '/assessments':   ['/assessments', '/reports'],
};

/**
 * Invalidate all cached entries whose key starts with any of the given prefixes.
 */
function invalidateByPrefixes(prefixes: string[]): void {
  Array.from(responseCache.keys()).forEach((key) => {
    for (const prefix of prefixes) {
      if (key.includes(prefix)) {
        responseCache.delete(key);
        break;
      }
    }
  });
}

/**
 * Given a mutated URL, determine which cache prefixes should be evicted.
 */
function getPrefixesToInvalidate(mutatedUrl: string): string[] {
  for (const [trigger, targets] of Object.entries(INVALIDATION_MAP)) {
    if (mutatedUrl.includes(trigger)) {
      return targets;
    }
  }
  // Fallback: only clear the exact resource
  return [mutatedUrl];
}

// ── Request Interceptor ────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    // Inject active JWT Bearer Token
    const token = localStorage.getItem('school_saas_token') || localStorage.getItem('superadminToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // For GET requests: serve from cache if still valid
    if (config.method?.toLowerCase() === 'get') {
      const fullUrl = api.getUri(config);
      const cached = responseCache.get(fullUrl);
      if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
        config.adapter = () => Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (cached)',
          headers: config.headers as any,
          config,
          request: {}
        });
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response Interceptor ───────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toLowerCase();

    if (method === 'get') {
      // Cache successful GET responses
      if (response.data && typeof response.data === 'object' && response.data.success !== false) {
        const fullUrl = api.getUri(response.config);
        responseCache.set(fullUrl, { data: response.data, timestamp: Date.now() });
      }
    } else {
      // Scoped invalidation: only bust keys related to the mutated resource
      const mutatedUrl = response.config.url ?? '';
      const prefixes = getPrefixesToInvalidate(mutatedUrl);
      invalidateByPrefixes(prefixes);

      // Also clear the school summary since counts may have changed
      invalidateByPrefixes(['/school/summary']);
    }

    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear expired credentials
      localStorage.removeItem('school_saas_token');
      localStorage.removeItem('school_saas_user');
      localStorage.removeItem('superadminToken');
      localStorage.removeItem('superadminUser');

      // Bounce back to login unless already there
      const isOnLogin = window.location.hash.includes('/login') || window.location.pathname.includes('/login');
      if (!isOnLogin) {
        window.location.hash = '#/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Deduplicated GET helper ────────────────────────────────────────────────
// Wrap api.get so that concurrent requests for the same URL share one Promise.
const _originalGet = api.get.bind(api);
api.get = function dedupedGet<T = any>(url: string, config?: AxiosRequestConfig): Promise<any> {
  const cacheKey = url + (config?.params ? JSON.stringify(config.params) : '');

  // If already in-flight, return the existing promise
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }

  const request = _originalGet<T>(url, config).finally(() => {
    inFlightRequests.delete(cacheKey);
  });

  inFlightRequests.set(cacheKey, request);
  return request;
} as typeof api.get;

export default api;
